'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { FiSend, FiCheckCircle, FiMail, FiLinkedin, FiTwitter } from 'react-icons/fi';
import { SocialLink, Form } from '@/shared/ui';
import type { IconType } from 'react-icons';
import { contactMock } from '@/shared/store/mocks/contact.mock';
import type { ContactSocialItem } from '@/shared/store/mocks/contact.mock';
import styles from './contact-drawer.module.scss';
import { useContactForm } from './useContactForm';
import { useScrollLock } from './ScrollLockContext';

const SOCIAL_ICONS: Record<ContactSocialItem['id'], IconType> = {
    email: FiMail,
    linkedin: FiLinkedin,
    twitter: FiTwitter,
};

const SWIPE_CLOSE_THRESHOLD_PX = 80;
const CLOSE_ANIMATION_MS = 350;

export interface ContactDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ContactDrawer({ isOpen, onClose }: ContactDrawerProps) {
    const { formData, fieldErrors, handleFieldChange, status, errorMessage, handleSubmit, isSubmitDisabled } =
        useContactForm();
    const { lock: scrollLock, unlock: scrollUnlock } = useScrollLock();
    const { form: formConfig, socials } = contactMock;
    const [isClosing, setIsClosing] = useState(false);
    const [isAnimatedOpen, setIsAnimatedOpen] = useState(false);
    const [closeSwipeTransform, setCloseSwipeTransform] = useState<string | null>(null);
    const [dragY, setDragY] = useState(0);
    const touchStartY = useRef<number | null>(null);
    const wasOpenRef = useRef(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isOpen) {
            wasOpenRef.current = true;
            let raf2: number;
            let raf3: number;
            const raf1 = requestAnimationFrame(() => {
                setIsAnimatedOpen(false);
                raf2 = requestAnimationFrame(() => {
                    raf3 = requestAnimationFrame(() => setIsAnimatedOpen(true));
                });
            });
            return () => {
                cancelAnimationFrame(raf1);
                if (raf2 != null) cancelAnimationFrame(raf2);
                if (raf3 != null) cancelAnimationFrame(raf3);
            };
        }
        const raf = requestAnimationFrame(() => setIsAnimatedOpen(false));
        return () => cancelAnimationFrame(raf);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen && wasOpenRef.current) {
            wasOpenRef.current = false;
            const raf = requestAnimationFrame(() => setIsClosing(true));
            return () => cancelAnimationFrame(raf);
        }
    }, [isOpen]);

    // Під час анімації закриття тримаємо scroll-lock через контекст (на мобільному — body overflow)
    useEffect(() => {
        if (isClosing || closeSwipeTransform) {
            scrollLock();
            return () => scrollUnlock();
        }
    }, [isClosing, closeSwipeTransform, scrollLock, scrollUnlock]);

    const finishClose = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        scrollUnlock();
        setIsClosing(false);
        setCloseSwipeTransform(null);
        setDragY(0);
        touchStartY.current = null;
        onClose();
    }, [onClose, scrollUnlock]);

    // Якщо transitionend не спрацює — знімаємо lock і закриваємо через CLOSE_ANIMATION_MS
    useEffect(() => {
        if (!isClosing && !closeSwipeTransform) return;
        closeTimeoutRef.current = setTimeout(() => {
            closeTimeoutRef.current = null;
            scrollUnlock();
            setIsClosing(false);
            setCloseSwipeTransform(null);
            setDragY(0);
            touchStartY.current = null;
            onClose();
        }, CLOSE_ANIMATION_MS);
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }
        };
    }, [isClosing, closeSwipeTransform, onClose, scrollUnlock]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) setIsClosing(true);
    };

    const handleTransitionEnd = (e: React.TransitionEvent) => {
        if (e.target !== e.currentTarget) return;
        if (e.propertyName === 'transform' && (isClosing || closeSwipeTransform)) finishClose();
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (closeSwipeTransform) return;
        touchStartY.current = e.touches[0].clientY;
        setDragY(0);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (touchStartY.current === null || closeSwipeTransform) return;
        const clientY = e.touches[0].clientY;
        const delta = Math.max(0, clientY - touchStartY.current);
        setDragY(delta);
    };

    const handleTouchEnd = () => {
        if (touchStartY.current === null) return;
        if (dragY >= SWIPE_CLOSE_THRESHOLD_PX) {
            touchStartY.current = null;
            setCloseSwipeTransform('100%');
        } else {
            setDragY(0);
            touchStartY.current = null;
        }
    };

    const visible = isOpen || isClosing || !!closeSwipeTransform;
    const wrapperOpen = isOpen && isAnimatedOpen && !isClosing && !closeSwipeTransform;
    const wrapperClosing = isClosing && !closeSwipeTransform;
    const panelStyle =
        closeSwipeTransform !== null
            ? { transform: `translateY(${closeSwipeTransform})` }
            : dragY > 0
              ? { transform: `translateY(${dragY}px)` }
              : undefined;

    if (!visible) return null;

    return (
        <>
            <div
                className={clsx(styles.overlay, wrapperOpen && styles.overlayOpen, (isClosing || closeSwipeTransform) && styles.overlayClosing)}
                onClick={handleOverlayClick}
                aria-hidden
            />
            <div
                className={clsx(styles.wrapper, wrapperOpen && styles.wrapperOpen, wrapperClosing && styles.wrapperClosing)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-drawer-title"
            >
                <div className={styles.panel} style={panelStyle} onTransitionEnd={handleTransitionEnd}>
                    <div
                        className={styles.handle}
                        aria-hidden
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                        style={{ touchAction: 'none' }}
                    />

                    <div className={styles.content}>
                        <h2 id="contact-drawer-title" className={styles.title}>
                            {formConfig.title}
                        </h2>
                        <p className={styles.subtitle}>{formConfig.subtitle}</p>

                        <div className={clsx(styles.formColumn, status === 'success' && styles.formColumnSuccess)}>
                            {status === 'success' && (
                                <div className={styles.successBanner} role="status">
                                    <FiCheckCircle size={48} className={styles.successIcon} />
                                    {formConfig.successMessage}
                                </div>
                            )}
                            <h3 className={styles.sideTitle}>{formConfig.formSideTitle}</h3>
                            <Form
                                fields={formConfig.fields}
                                values={formData}
                                errors={fieldErrors}
                                onChange={handleFieldChange}
                                onSubmit={handleSubmit}
                                honeypotName={formConfig.honeypotName}
                                status={status}
                                errorMessage={errorMessage}
                                submitLabel={formConfig.submitLabel}
                                sendingLabel={formConfig.sendingLabel}
                                isSubmitDisabled={isSubmitDisabled}
                                submitEndIcon={<FiSend size={18} />}
                                classNames={{
                                    form: styles.form,
                                    honeypot: styles.honeypot,
                                    fieldRoot: styles.fieldRoot,
                                    fieldRootError: styles.fieldRootError,
                                    input: styles.input,
                                    textarea: styles.textarea,
                                    error: styles.fieldError,
                                    submitBtn: styles.submitBtn,
                                }}
                            />
                        </div>

                        <div className={styles.socialsTitle}>{formConfig.socialsTitle}</div>
                        <div className={styles.socials}>
                            {socials.map((s) => (
                                <SocialLink key={s.id} href={s.href} icon={SOCIAL_ICONS[s.id]} label={s.label} size={20} className={styles.socialLink} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
